import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Chip,
  Grid,
  Paper,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import { useId, useState } from 'react';
// eslint-disable-next-line @backstage/no-relative-monorepo-imports
import type {
  AssessmentDestination,
  TargetIntentDraft,
  AssessmentDestinationRequest,
} from '../../../../../contracts/assess/target';
import type { InstanceCandidate } from './InstanceSelectionTable';

export function normalizeImageIntent(
  value?: TargetIntentDraft,
): TargetIntentDraft {
  const requestedAmiId = value?.requestedAmiId?.trim() || null;
  return {
    schemaVersion: 'assess-target-intent/1',
    requestedAmiId,
    desiredOs: requestedAmiId ? value?.desiredOs ?? null : null,
    destination: value?.destination ?? null,
    ...(value?.destinationRequest
      ? { destinationRequest: value.destinationRequest }
      : {}),
  };
}

export type DestinationConnection = {
  id: string;
  title: string;
  accountId: string;
  status: string;
};
const useStyles = makeStyles(theme => ({
  card: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
    borderRadius: 12,
    minWidth: 0,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  source: {
    padding: theme.spacing(1.5, 2),
    margin: theme.spacing(2, 0),
    background: theme.palette.action.hover,
    borderRadius: 8,
  },
  section: { marginTop: theme.spacing(3) },
  disclosure: {
    marginTop: theme.spacing(2),
    '& > summary': {
      cursor: 'pointer',
      fontWeight: 500,
      padding: theme.spacing(1, 0),
    },
  },
}));

/** Same-family/distribution/architecture review is necessary, not provider compatibility proof. */
export function compatibleImageSubset(sources: InstanceCandidate[]) {
  if (!sources.length) return false;
  const first = sources[0];
  return sources.every(
    item =>
      item.architecture &&
      item.architecture === first.architecture &&
      item.sourceOs &&
      ['VERIFIED', 'LIMITED'].includes(item.sourceOs.status) &&
      ['LINUX', 'WINDOWS'].includes(item.sourceOs.family) &&
      item.sourceOs.family === first.sourceOs?.family &&
      !!item.sourceOs.distribution &&
      item.sourceOs.distribution === first.sourceOs?.distribution,
  );
}

/** Reused source/destination presentation of one controlled canonical draft; no service or local-storage side effects. */
export function TargetIntentEditor({
  value,
  onChange,
  source,
  disabled,
  destinationDefaults,
  allowedSubnetIds = [],
  allowedSecurityGroupIds = [],
  connections = [],
  connectionError,
}: {
  value?: TargetIntentDraft;
  onChange: (value: TargetIntentDraft) => void;
  source: InstanceCandidate;
  disabled?: boolean;
  /** Only server-returned approved profile defaults may be supplied here. */
  destinationDefaults?: Partial<AssessmentDestination>;
  allowedSubnetIds?: string[];
  allowedSecurityGroupIds?: string[];
  connections?: DestinationConnection[];
  connectionError?: string;
}) {
  const id = useId();
  const classes = useStyles();
  const draft = normalizeImageIntent(value);
  const [enabled, setEnabled] = useState(Boolean(draft.requestedAmiId));
  const [mapping, setMapping] = useState<Partial<AssessmentDestination>>(
    () => draft.destination ?? destinationDefaults ?? {},
  );
  const custom = enabled || Boolean(draft.requestedAmiId);
  const request = draft.destinationRequest;
  const [destinationEnabled, setDestinationEnabled] = useState(
    Boolean(request),
  );
  const [destinationText, setDestinationText] = useState<
    Record<string, string>
  >({});
  const editDestination = (change: Partial<AssessmentDestinationRequest>) => {
    onChange({
      ...draft,
      destination: null,
      destinationRequest: { provider: 'aws', ...request, ...change },
    });
  };
  const requestedProvider = request?.provider ?? 'aws';
  const fields: {
    key: keyof AssessmentDestinationRequest;
    label: string;
    helper?: string;
    awsOnly?: boolean;
    multiline?: boolean;
  }[] = [
    {
      key: 'accountId',
      label:
        requestedProvider === 'aws'
          ? 'Destination AWS account ID'
          : 'Destination account / subscription / project',
      helper:
        requestedProvider === 'aws'
          ? '12-digit account ID. Must match the authorized connection.'
          : 'Client account, subscription or project reference.',
    },
    {
      key: 'region',
      label: 'Destination Region',
      helper: 'For example, us-west-2 for AWS.',
    },
    {
      key: requestedProvider === 'aws' ? 'vpcId' : 'networkId',
      label:
        requestedProvider === 'aws'
          ? 'Destination VPC ID'
          : 'Destination network reference',
    },
    { key: 'subnetId', label: 'Destination subnet' },
    {
      key: 'securityGroupIds',
      label:
        requestedProvider === 'aws'
          ? 'Destination security group IDs'
          : 'Destination security / firewall references',
      helper: 'Separate multiple IDs with commas.',
    },
    { key: 'instanceType', label: 'Destination instance / machine type' },
  ];
  const optionalFields: typeof fields = [
    { key: 'testSubnetId', label: 'Isolated test subnet' },
    {
      key: 'testSecurityGroupIds',
      label: 'Isolated test security group IDs',
      helper: 'Separate multiple IDs with commas.',
    },
    { key: 'kmsKeyArn', label: 'Encryption key ARN', awsOnly: true },
    {
      key: 'iamInstanceProfileArn',
      label: 'IAM instance profile ARN',
      awsOnly: true,
    },
    {
      key: 'storageConfigurationRef',
      label: 'Storage configuration reference',
    },
    { key: 'rootVolumeGiB', label: 'Root volume size (GiB)', awsOnly: true },
  ];
  const destinationField = (field: (typeof fields)[number]) => (
    <Grid item xs={12} md={6} key={field.key}>
      <TextField
        id={`${id}-request-${field.key}`}
        label={field.label}
        fullWidth
        variant="outlined"
        size="small"
        InputLabelProps={{ shrink: true }}
        disabled={disabled}
        type={field.key === 'rootVolumeGiB' ? 'number' : 'text'}
        value={
          destinationText[field.key] ??
          (Array.isArray(request?.[field.key])
            ? (request?.[field.key] as string[]).join(', ')
            : request?.[field.key] ?? '')
        }
        helperText={field.helper}
        onChange={event => {
          const valueText = event.target.value;
          setDestinationText(previous => ({
            ...previous,
            [field.key]: valueText,
          }));
          const next = {
            ...request,
            provider: requestedProvider,
          } as AssessmentDestinationRequest;
          if (!valueText.trim()) delete next[field.key];
          else if (
            field.key === 'securityGroupIds' ||
            field.key === 'testSecurityGroupIds'
          )
            next[field.key] = valueText
              .split(',')
              .map(item => item.trim())
              .filter(Boolean);
          else if (field.key === 'rootVolumeGiB')
            next.rootVolumeGiB = Number(valueText);
          else (next as Record<string, unknown>)[field.key] = valueText;
          onChange({ ...draft, destination: null, destinationRequest: next });
        }}
      />
    </Grid>
  );
  const updateImage = (requestedAmiId: string) =>
    onChange(normalizeImageIntent({ ...draft, requestedAmiId }));
  const os = source.sourceOs;
  const mappingFields = [
    'connectionId',
    'accountId',
    'region',
    'vpcId',
    'landingZoneVersion',
    'instanceType',
    'storageConfigurationRef',
    'kmsKeyArn',
    'iamInstanceProfileArn',
    'testSubnetId',
  ] as const;
  const complete = (
    candidate: Partial<AssessmentDestination>,
  ): candidate is AssessmentDestination =>
    [
      'connectionId',
      'accountId',
      'region',
      'vpcId',
      'subnetId',
      'landingZoneVersion',
      'instanceType',
    ].every(key => Boolean(candidate[key as keyof AssessmentDestination])) &&
    !!candidate.securityGroupIds?.length;
  const pendingMapping = { ...destinationDefaults, ...mapping };
  const profileIdentityAvailable = [
    'connectionId',
    'accountId',
    'region',
    'vpcId',
    'landingZoneVersion',
  ].every(key =>
    Boolean(destinationDefaults?.[key as keyof AssessmentDestination]),
  );
  const profileMismatch = mappingFields.some(
    field =>
      destinationDefaults?.[field] !== undefined &&
      mapping[field] !== undefined &&
      mapping[field] !== destinationDefaults[field],
  );
  const validChoices =
    !!pendingMapping.subnetId &&
    allowedSubnetIds.includes(pendingMapping.subnetId) &&
    !!pendingMapping.securityGroupIds?.length &&
    pendingMapping.securityGroupIds.every(group =>
      allowedSecurityGroupIds.includes(group),
    );
  return (
    <Paper variant="outlined" className={classes.card}>
      <Box className={classes.header}>
        <Box>
          <Typography variant="h6">
            {source.name ?? source.instanceId}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {source.instanceId} · {source.accountId || 'Account not recorded'} ·{' '}
            {source.region || 'Region not recorded'}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={
            draft.destinationRequest || draft.destination
              ? 'Destination draft'
              : 'Source discovery'
          }
        />
      </Box>
      <Box className={classes.source}>
        <Typography variant="body2">
          Source OS (read-only): {os?.family ?? 'Unknown'} |{' '}
          {os?.distribution ?? 'Distribution unknown'} |{' '}
          {os?.version ?? 'Version unknown—guest evidence required'} ·{' '}
          {source.architecture ?? 'Architecture unknown'}
        </Typography>
        {source.launchImageId && (
          <Typography variant="caption">
            Source launch AMI: {source.launchImageId} (not current OS proof)
          </Typography>
        )}
        <Typography variant="caption" color="textSecondary">
          OS evidence: {os?.status ?? 'UNAVAILABLE'}.{' '}
          {os?.status === 'LIMITED'
            ? 'Reported observation only; current OS/kernel requires fresh, authorized evidence.'
            : os?.reason ?? 'Current operating system has not been verified.'}
        </Typography>
      </Box>
      {!destinationDefaults && (
        <Box className={classes.section}>
          <Typography variant="subtitle1">Destination environment</Typography>
          <Typography variant="body2" color="textSecondary">
            Enter your client destination. You can save incomplete details;
            assessment will show which decisions or permissions are still
            needed.
          </Typography>
          <FormControlLabel
            label="Add destination details (optional)"
            control={
              <Checkbox
                checked={destinationEnabled || Boolean(request)}
                disabled={disabled}
                onChange={(_, checked) => {
                  setDestinationEnabled(checked);
                  if (checked) editDestination({ provider: requestedProvider });
                  else {
                    const { destinationRequest: _removed, ...rest } = draft;
                    onChange({ ...rest, destination: null });
                    setDestinationText({});
                  }
                }}
              />
            }
          />
          {(destinationEnabled || request) && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    id={`${id}-provider`}
                    label="Destination provider"
                    select
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={disabled}
                    value={requestedProvider}
                    onChange={event => {
                      const provider = event.target
                        .value as AssessmentDestinationRequest['provider'];
                      const next = { ...request, provider };
                      delete next.connectionId;
                      onChange({
                        ...draft,
                        destination: null,
                        destinationRequest: next,
                      });
                    }}
                  >
                    <option value="aws">Amazon Web Services (AWS)</option>
                    <option value="azure">
                      Microsoft Azure — planning only
                    </option>
                    <option value="gcp">Google Cloud — planning only</option>
                    <option value="other">
                      Other provider — planning only
                    </option>
                  </TextField>
                </Grid>
                {requestedProvider === 'other' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      id={`${id}-provider-name`}
                      label="Provider name"
                      fullWidth
                      variant="outlined"
                      size="small"
                      disabled={disabled}
                      value={request?.providerName ?? ''}
                      onChange={event => {
                        const next = {
                          ...request,
                          provider: requestedProvider,
                        };
                        if (event.target.value.trim())
                          next.providerName = event.target.value;
                        else delete next.providerName;
                        onChange({
                          ...draft,
                          destination: null,
                          destinationRequest: next,
                        });
                      }}
                    />
                  </Grid>
                )}
                {requestedProvider === 'aws' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      id={`${id}-connection`}
                      label="Authorized destination connection"
                      select
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      size="small"
                      fullWidth
                      disabled={disabled}
                      value={request?.connectionId ?? ''}
                      helperText={
                        connectionError ||
                        'Uses your existing account connection. Credentials are never entered here.'
                      }
                      onChange={event => {
                        const connection = connections.find(
                          item => item.id === event.target.value,
                        );
                        const next = { ...request, provider: 'aws' as const };
                        delete next.connectionId;
                        if (connection) {
                          next.connectionId = connection.id;
                          next.accountId = connection.accountId;
                        }
                        setDestinationText(previous => {
                          const nextText = { ...previous };
                          delete nextText.accountId;
                          return nextText;
                        });
                        onChange({
                          ...draft,
                          destination: null,
                          destinationRequest: next,
                        });
                      }}
                    >
                      <option value="">Choose an authorized connection</option>
                      {connections.map(connection => (
                        <option key={connection.id} value={connection.id}>
                          {connection.title} · {connection.accountId} ·{' '}
                          {connection.status}
                        </option>
                      ))}
                      {request?.connectionId &&
                        !connections.some(
                          item => item.id === request.connectionId,
                        ) && (
                          <option value={request.connectionId}>
                            {request.connectionId} — availability not verified
                          </option>
                        )}
                    </TextField>
                  </Grid>
                )}
                {fields.map(destinationField)}
              </Grid>
              <details className={classes.disclosure}>
                <summary>
                  Test network, storage and encryption (optional)
                </summary>
                <Grid container spacing={2}>
                  {optionalFields
                    .filter(
                      field => !field.awsOnly || requestedProvider === 'aws',
                    )
                    .map(destinationField)}
                </Grid>
              </details>
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary">
                  {requestedProvider === 'aws'
                    ? 'Saved as a client declaration. Horizon validates the account, Region and network before using this destination. Approval and MOVE preflight are separate.'
                    : 'This provider can be recorded for planning. A supported execution adapter and authorized connection are required before migration.'}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      )}
      <Box className={classes.section}>
        <Typography variant="subtitle1">
          Target operating system and image
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Keep the source operating system by default, or record a custom image
          to evaluate.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={custom}
              disabled={disabled || (!custom && requestedProvider !== 'aws')}
              onChange={(_, checked) => {
                setEnabled(checked);
                if (!checked) updateImage('');
              }}
            />
          }
          label="Use custom target AMI / change target OS version"
        />
        <TextField
          id={`${id}-ami`}
          fullWidth
          label="Target AMI override (optional)"
          value={draft.requestedAmiId ?? ''}
          disabled={disabled || !custom || requestedProvider !== 'aws'}
          variant="outlined"
          size="small"
          onChange={event => updateImage(event.target.value)}
          helperText="Leave blank to request no OS-image replacement. Supplying an AMI requests an image-based target; it does not change the source server. Migration-method validation still applies."
        />
        <Typography role="status" variant="body2" color="textSecondary">
          {draft.requestedAmiId
            ? 'Custom AMI requested. Image compatibility and a supported migration method must be verified.'
            : 'Keep source OS — no replacement image requested.'}
        </Typography>
        {draft.requestedAmiId && requestedProvider !== 'aws' && (
          <Typography color="error" role="alert">
            The custom AMI is retained for review. AMIs apply only to AWS; clear
            the image selection or choose AWS before submission.
          </Typography>
        )}
        {draft.requestedAmiId && (
          <>
            <TextField
              id={`${id}-family`}
              label="Intended target OS family (client declaration)"
              select
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              disabled={disabled}
              variant="outlined"
              fullWidth
              value={draft.desiredOs?.family ?? ''}
              onChange={event =>
                onChange({
                  ...draft,
                  desiredOs: event.target.value
                    ? {
                        family: event.target.value as 'linux' | 'windows',
                        distribution: draft.desiredOs?.distribution ?? null,
                        version: draft.desiredOs?.version ?? null,
                      }
                    : null,
                })
              }
            >
              <option value="">Not declared</option>
              <option value="linux">Linux</option>
              <option value="windows">Windows</option>
            </TextField>
            {(['distribution', 'version'] as const).map(field => (
              <TextField
                key={field}
                id={`${id}-${field}`}
                label={`Intended target OS ${field} (client declaration)`}
                value={draft.desiredOs?.[field] ?? ''}
                disabled={disabled || !draft.desiredOs}
                variant="outlined"
                fullWidth
                margin="normal"
                onChange={event =>
                  onChange({
                    ...draft,
                    desiredOs: draft.desiredOs
                      ? {
                          ...draft.desiredOs,
                          [field]: event.target.value.trim() || null,
                        }
                      : null,
                  })
                }
              />
            ))}
            <Typography variant="body2">
              {draft.destination
                ? 'Pending server-side target/image validation against the selected destination.'
                : 'Pending target-context validation.'}{' '}
              Image names, tags and client declarations do not verify image
              contents, patching or application compatibility.
            </Typography>
            {draft.desiredOs &&
              os &&
              ['LINUX', 'WINDOWS'].includes(os.family) &&
              draft.desiredOs.family.toUpperCase() !== os.family && (
                <Typography color="error" role="alert">
                  Source and intended target OS families differ. This route does
                  not support cross-family conversion; server validation must
                  block execution.
                </Typography>
              )}
          </>
        )}
      </Box>
      {destinationDefaults && (
        <Box mt={2}>
          <Typography variant="subtitle2">
            Approved destination mapping
          </Typography>
          <Typography variant="body2">
            Defaults and locked values come from the selected approved landing
            zone. Editing this draft invalidates prior validation. No resources
            are provisioned.
          </Typography>
          {mappingFields.map(field => (
            <TextField
              key={field}
              id={`${id}-${field}`}
              label={`Target ${field}`}
              value={pendingMapping[field] ?? ''}
              disabled
              helperText={
                destinationDefaults[field] !== undefined
                  ? 'Locked by the approved landing-zone profile'
                  : 'Not supplied by the approved profile; remains pending'
              }
            />
          ))}
          <TextField
            id={`${id}-subnet`}
            label="Target subnet"
            select
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            value={pendingMapping.subnetId ?? ''}
            disabled={disabled || allowedSubnetIds.length === 1}
            onChange={event =>
              setMapping(previous => ({
                ...previous,
                subnetId: event.target.value,
              }))
            }
          >
            <option value="">Select approved subnet</option>
            {allowedSubnetIds.map(subnet => (
              <option key={subnet} value={subnet}>
                {subnet}
              </option>
            ))}
            {pendingMapping.subnetId &&
              !allowedSubnetIds.includes(pendingMapping.subnetId) && (
                <option value={pendingMapping.subnetId}>
                  {pendingMapping.subnetId} — no longer approved
                </option>
              )}
          </TextField>
          {allowedSecurityGroupIds.map(group => (
            <FormControlLabel
              key={group}
              label={`Target security group ${group}`}
              control={
                <Checkbox
                  disabled={disabled || allowedSecurityGroupIds.length === 1}
                  checked={
                    pendingMapping.securityGroupIds?.includes(group) ?? false
                  }
                  onChange={(_, checked) =>
                    setMapping(previous => ({
                      ...previous,
                      securityGroupIds: checked
                        ? [
                            ...new Set([
                              ...(previous.securityGroupIds ?? []),
                              group,
                            ]),
                          ]
                        : (previous.securityGroupIds ?? []).filter(
                            valueId => valueId !== group,
                          ),
                    }))
                  }
                />
              }
            />
          ))}
          <Button
            disabled={
              disabled ||
              !profileIdentityAvailable ||
              profileMismatch ||
              !complete(pendingMapping) ||
              !validChoices
            }
            onClick={() => {
              if (complete(pendingMapping) && validChoices)
                onChange({ ...draft, destination: pendingMapping });
            }}
          >
            Apply reviewed destination to this server
          </Button>
          {profileMismatch && (
            <Box role="alert">
              <Typography color="error">
                Stored destination differs from the current approved profile. It
                is retained but cannot be applied without review.
              </Typography>
              <Button
                disabled={disabled}
                onClick={() => {
                  setMapping(destinationDefaults);
                  onChange({ ...draft, destination: null });
                }}
              >
                Use current approved destination defaults
              </Button>
            </Box>
          )}
          {!profileIdentityAvailable && (
            <Typography color="error">
              Approved destination connection/version is unavailable. Concrete
              mapping remains pending; no source credentials or profile version
              are substituted.
            </Typography>
          )}
          {!complete(pendingMapping) && (
            <Typography color="error">
              Destination incomplete. Required approved defaults must be
              supplied; no network or instance-type substitute is chosen.
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
}
